import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { 
  Heart, FileText, ClipboardCheck, Sparkles, Calendar,
  CheckCircle, ArrowRight, ArrowLeft, X, Loader2,
  User, Clock, Target, Lightbulb, Play, PartyPopper,
  ChevronRight, GripVertical, Plus, Share2, MessageSquare, UserPlus
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// Demo child profile
const DEMO_CHILD = {
  name: 'Alex Thompson',
  age: 5,
  dob: '2021-03-15',
  diagnosis: 'Developmental Language Disorder, Gestalt Language Processor',
  goals: [
    'Progress through NLA stages—expand mitigations toward self-generated language',
    'Support progression through NLA stages toward self-generated language',
    'Improve speech intelligibility for /s/, /r/ sounds in naturalistic contexts'
  ],
  interests: ['dinosaurs', 'building blocks', 'water play'],
  glpStage: 'Stage 2-3',
  communicationStyle: 'Gestalt Language Processor'
};

// Pre-filled assessment data for clinical report
const DEMO_ASSESSMENT_DATA = {
  standardizedTests: [
    { name: 'CELF-5', area: 'Receptive Language', standardScore: 78, percentile: 7, descriptor: 'Below Average' },
    { name: 'CELF-5', area: 'Expressive Language', standardScore: 72, percentile: 3, descriptor: 'Below Average' },
    { name: 'GFTA-3', area: 'Articulation', standardScore: 75, percentile: 5, descriptor: 'Below Average' }
  ],
  informalMeasures: [
    { name: 'Language Sample Analysis', area: 'Expressive Language', notes: 'Uses rich gestalt phrases in play contexts. Functioning at Stage 2-3 on NLA framework with emerging mitigations.' },
    { name: 'Play-Based Observation', area: 'Pragmatics', notes: 'Engages in parallel play, emerging joint attention. Responds to indirect models.' }
  ],
  clinicalObservations: 'Alex presents as a gestalt language processor at NLA Stage 2-3, using meaningful multi-word units in context-dependent ways.'
};

const STEPS = [
  { id: 'report', title: 'Report', icon: FileText, description: 'Generate AI Report' },
  { id: 'soap', title: 'SOAP Note', icon: ClipboardCheck, description: 'Save Documentation' },
  { id: 'activities', title: 'Activities', icon: Sparkles, description: 'AI Activity Ideas' },
  { id: 'scheduling', title: 'Daysheet', icon: Calendar, description: 'View Schedule' }
];

// Days of week for daysheet
const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

export default function DemoFlow({ onComplete, onExit }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [demoSessionId, setDemoSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Step 1: Report - Clinical Assessment Data
  const [assessmentData, setAssessmentData] = useState(DEMO_ASSESSMENT_DATA);
  const [userObservation, setUserObservation] = useState('');
  const [generatedReport, setGeneratedReport] = useState(null);
  
  // Step 2: SOAP
  const [soapNote, setSoapNote] = useState({
    subjective: 'Alex arrived happy and engaged. Parent reported increased spontaneous phrases at home this week.',
    objective: '',
    assessment: '',
    plan: ''
  });
  const [soapSaved, setSoapSaved] = useState(false);
  const [soapDate, setSoapDate] = useState('');
  const [parentSummary, setParentSummary] = useState('');
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [shareWithParent, setShareWithParent] = useState(false);
  
  // Step 3: Activities
  const [generatedActivities, setGeneratedActivities] = useState([]);
  
  // Step 4: Daysheet/Scheduling
  const [daysheetEntries, setDaysheetEntries] = useState([]);
  const [appointmentConfirmed, setAppointmentConfirmed] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('10:00');
  
  // Signup popup state
  const [showSignupPrompt, setShowSignupPrompt] = useState(false);
  const [signupPromptMessage, setSignupPromptMessage] = useState('');

  // Initialize demo session
  useEffect(() => {
    const initDemo = async () => {
      try {
        const response = await axios.post(`${API}/demo/start`);
        setDemoSessionId(response.data.session_id);
      } catch (e) {
        setDemoSessionId(`demo_${Date.now()}`);
      }
    };
    initDemo();
    
    // Set today's date for SOAP
    const today = new Date();
    setSoapDate(today.toISOString().split('T')[0]);
    
    // Set default appointment date (next weekday)
    const nextDate = new Date();
    nextDate.setDate(nextDate.getDate() + 7); // One week from now
    setSelectedDate(nextDate.toISOString().split('T')[0]);
  }, []);

  const trackEvent = async (event, metadata = {}) => {
    try {
      await axios.post(`${API}/demo/track-event`, {
        session_id: demoSessionId,
        event,
        metadata
      });
    } catch (e) {
      // Silent fail for tracking
    }
  };

  // Step 1: Generate Report from Clinical Assessment Data
  const handleGenerateReport = async () => {
    setLoading(true);
    
    try {
      await trackEvent('report_generated');
      
      // Simulate AI report generation from assessment data
      await new Promise(resolve => setTimeout(resolve, 2500));
      
      setGeneratedReport({
        sections: {
          background: `Alex Thompson is a 5-year-old who was referred for a comprehensive speech-language evaluation due to concerns regarding language development and speech intelligibility. Alex has been identified as a Gestalt Language Processor, currently functioning at NLA Stage 2-3.`,
          
          assessmentResults: `Standardized Assessment Results:

CELF-5 (Clinical Evaluation of Language Fundamentals, 5th Edition):
• Receptive Language: Standard Score 78 (7th percentile) - Below Average
• Expressive Language: Standard Score 72 (3rd percentile) - Below Average

GFTA-3 (Goldman-Fristoe Test of Articulation, 3rd Edition):
• Articulation: Standard Score 75 (5th percentile) - Below Average

Informal Measures:

Language Sample Analysis: Alex uses rich gestalt phrases meaningfully in play contexts. He is functioning at Stage 2-3 on the Natural Language Acquisition (NLA) framework, demonstrating emerging mitigations—mixing and matching parts of familiar gestalts to create new meanings.

Play-Based Observation: Engages in parallel play with emerging joint attention. Responds positively to indirect language models and child-led interaction.`,
          
          clinicalImpression: `Alex presents as a gestalt language processor at NLA Stage 2-3, demonstrating strengths in using meaningful multi-word units within familiar contexts. He demonstrated strong engagement throughout testing.

Standardized Testing Considerations:
Alex's CELF-5 scores (Receptive Language: 78, 7th percentile; Expressive Language: 72, 3rd percentile) fall in the below average range. However, it is important to interpret these results with caution. Standardized language assessments like the CELF-5 are normed primarily on analytic language learners and may not accurately capture the language abilities of gestalt language processors. Alex's rich use of contextually-appropriate gestalts and his meaningful communication during play-based interactions suggest language competencies that are not fully reflected in these standardized scores. His GFTA-3 articulation score (75, 5th percentile) indicates speech sound development is an area for continued support.

From a neurodiversity-affirming perspective, Alex's gestalt processing style represents a valid and meaningful path to language development. His use of rich, contextually-appropriate gestalts demonstrates strong pattern recognition and holistic language learning.

Strengths:
Alex demonstrates notable strengths in interest-based engagement, showing sustained attention and motivation during activities involving dinosaurs, water play, and building. He uses gestalt phrases meaningfully within naturalistic contexts, indicating a solid foundation for language development through the GLP framework. Alex responds positively to child-led, play-based interactions and shows emerging ability to mitigate (break down) his gestalts into smaller, more flexible units—a key indicator of progression through the NLA stages.

Areas for Support:
Alex would benefit from continued support in expanding from Stage 2 (mitigations—mixing and matching parts of gestalts) toward Stage 3 (isolating single words and creating original two-word combinations) as he develops more self-generated language. Speech sound development, particularly for /s/ and /r/, should be addressed within meaningful play contexts rather than isolated drill. Additionally, supporting the generalization of language skills across different environments and communication partners will help Alex transfer his abilities beyond familiar routines.`,
          
          recommendations: `Recommendations:

1. Continue with a naturalistic, play-based intervention approach aligned with GLP/NLA principles
2. Provide language models at the "edge" of Alex's current stage to support mitigations
3. Follow Alex's lead and interests to maximize engagement and meaningful language use
4. Address articulation goals within play contexts rather than drill-based practice
5. Parent coaching focused on responsive interaction strategies and supporting language development at home
6. Re-evaluation in 6 months to monitor progress through NLA stages`
        },
        userObservation
      });
      
      toast.success('Report generated!');
      
      // Pre-fill SOAP note for next step
      setSoapNote(prev => ({
        ...prev,
        objective: 'Completed comprehensive evaluation. Standardized testing (CELF-5, GFTA-3) and informal measures administered. Language sample collected during play-based observation.',
        assessment: 'Results consistent with Developmental Language Disorder. Alex presents as a gestalt language processor at NLA Stage 2-3 with relative strengths in contextual language use and interest-based engagement.',
        plan: 'Recommend weekly speech-language therapy using naturalistic, GLP-aligned intervention. Focus on gestalt expansion and articulation within play contexts. Parent coaching component included.'
      }));
      
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Generate AI Parent Summary
  const handleGenerateParentSummary = async () => {
    setGeneratingSummary(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Simulated AI-generated parent-friendly summary
      const summary = `Hi! Today ${DEMO_CHILD.name} and I worked on expanding his phrases and practicing clearer speech sounds.

🌟 What we did:
• Played with dinosaurs while I modeled new phrases like "dinosaur is jumping" and "more blocks please"
• Practiced the /s/ sound in fun words like "splash" and "stomp"
• Alex spontaneously said "let's play more!" - a big win!

🏠 At home, you can try:
• During play, model phrases like "I want the big dinosaur" and wait to see if Alex tries any part of it
• Read dinosaur books and pause to let him fill in words
• Celebrate any new phrases he tries, even if not perfect!

Alex is doing great with his language development. His gestalt phrases are becoming more flexible, which is exactly what we want to see!`;
      
      setParentSummary(summary);
      toast.success('Parent summary generated!');
      
    } catch (error) {
      toast.error('Failed to generate summary');
    } finally {
      setGeneratingSummary(false);
    }
  };

  // Step 2: Save SOAP Note
  const handleSaveSoap = async () => {
    setLoading(true);
    
    try {
      await trackEvent('soap_saved');
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Add entry to daysheet
      const today = new Date();
      const dayOfWeek = today.getDay(); // 0=Sun, 1=Mon, etc.
      const dayIndex = dayOfWeek === 0 ? 4 : dayOfWeek - 1; // Convert to Mon=0, Fri=4
      
      setDaysheetEntries([{
        id: 'demo-entry-1',
        dayIndex: Math.min(dayIndex, 4), // Keep within Mon-Fri
        childName: DEMO_CHILD.name,
        time: '10:00',
        status: 'completed',
        fromSoap: true
      }]);
      
      setSoapSaved(true);
      toast.success('SOAP note saved and added to daysheet!');
      
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Generate Activities
  const handleGenerateActivities = async () => {
    setLoading(true);
    
    try {
      await trackEvent('activities_generated');
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setGeneratedActivities([
        {
          title: 'Dinosaur Splash Station',
          description: 'Set up a water table with plastic dinosaurs. Model phrases like "dinosaur splashing!" and "big splash, little splash" to expand Alex\'s gestalt repertoire while targeting /s/ sounds.',
          goals: ['Gestalt expansion', '/s/ production'],
          materials: ['Water table', 'Plastic dinosaurs', 'Cups']
        },
        {
          title: 'Dino Construction Zone',
          description: 'Build dinosaur habitats with blocks. Use language like "stack more blocks" and "dinosaur house is tall" to model 4+ word phrases Alex can begin to mitigate.',
          goals: ['Gestalt expansion', 'Vocabulary'],
          materials: ['Building blocks', 'Dinosaur figures']
        },
        {
          title: 'Sensory Dino Dig',
          description: 'Hide dinosaurs in sensory bin (rice/sand). Target phrases like "I found the red one!" and "dig, dig, dig for dinosaurs" with natural repetition.',
          goals: ['Gestalt expansion', 'Turn-taking'],
          materials: ['Sensory bin', 'Rice or sand', 'Small dinosaurs']
        }
      ]);
      
      toast.success('Activities generated!');
      
    } catch (error) {
      toast.error('Failed to generate activities');
    } finally {
      setLoading(false);
    }
  };

  // Step 4: View Daysheet (shows auto-populated entry from SOAP)
  const handleViewDaysheet = async () => {
    await trackEvent('scheduling_completed');
    await trackEvent('demo_completed');
  };

  // Step 4: Confirm appointment
  const handleConfirmAppointment = async () => {
    if (!selectedDate) {
      toast.error('Please select a date');
      return;
    }
    
    setLoading(true);
    
    try {
      await trackEvent('scheduling_completed');
      await trackEvent('demo_completed');
      
      // Simulate saving appointment
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Add new entry to daysheet
      const date = new Date(selectedDate);
      const dayIndex = date.getDay() === 0 ? 4 : date.getDay() - 1; // Mon=0, Fri=4
      
      setDaysheetEntries(prev => [...prev, {
        id: 'demo-entry-scheduled',
        dayIndex: Math.min(dayIndex, 4),
        childName: DEMO_CHILD.name,
        time: selectedTime,
        status: 'scheduled',
        fromSoap: false
      }]);
      
      setAppointmentConfirmed(true);
      toast.success('Appointment scheduled!');
      
    } catch (error) {
      toast.error('Failed to schedule');
    } finally {
      setLoading(false);
    }
  };

  const goToNextStep = () => {
    if (currentStep < STEPS.length - 1) {
      // Show signup prompt after each section
      const stepMessages = [
        "Love generating AI reports? Create an account to save and customize reports for your real clients!",
        "Your SOAP notes would be saved automatically! Sign up to start documenting with ease.",
        "These AI activities could be personalized for YOUR clients! Ready to create your free account?",
      ];
      
      if (currentStep < stepMessages.length) {
        setSignupPromptMessage(stepMessages[currentStep]);
        setShowSignupPrompt(true);
      } else {
        setCurrentStep(prev => prev + 1);
      }
    }
  };
  
  const continueToNextStep = () => {
    setShowSignupPrompt(false);
    setCurrentStep(prev => prev + 1);
  };

  const goToPrevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const progress = ((currentStep + 1) / STEPS.length) * 100;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-sm z-50 border-b border-stone-100">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Heart className="w-4 h-4 text-primary" />
              </div>
              <span className="font-serif text-primary-900">PlayFirst Demo</span>
            </div>
            <Button variant="ghost" size="sm" onClick={onExit} className="rounded-full">
              <X className="w-4 h-4 mr-1" /> Exit Demo
            </Button>
          </div>
          
          {/* Progress Bar */}
          <div className="flex items-center gap-2">
            {STEPS.map((step, idx) => (
              <div key={step.id} className="flex-1 flex items-center">
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  idx < currentStep ? 'bg-green-100 text-green-700' :
                  idx === currentStep ? 'bg-primary text-white' :
                  'bg-stone-100 text-stone-500'
                }`}>
                  {idx < currentStep ? (
                    <CheckCircle className="w-3 h-3" />
                  ) : (
                    <step.icon className="w-3 h-3" />
                  )}
                  <span className="hidden sm:inline">{step.title}</span>
                </div>
                {idx < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-1 ${idx < currentStep ? 'bg-green-300' : 'bg-stone-200'}`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-28 pb-24 px-4">
        <div className="max-w-4xl mx-auto">
          <AnimatePresence mode="wait">
            {/* Step 1: Report Generation */}
            {currentStep === 0 && (
              <motion.div
                key="report"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                {/* Demo Child Card */}
                <Card className="p-4 mb-6 bg-gradient-to-r from-primary/5 to-primary/10 rounded-2xl border-0">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                      <User className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium text-primary-900">{DEMO_CHILD.name}</h3>
                        <Badge variant="secondary" className="text-xs">{DEMO_CHILD.age} years old</Badge>
                        <Badge className="text-xs bg-purple-100 text-purple-700">{DEMO_CHILD.glpStage}</Badge>
                      </div>
                      <p className="text-sm text-stone-600">{DEMO_CHILD.diagnosis}</p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {DEMO_CHILD.interests.map(interest => (
                          <Badge key={interest} variant="outline" className="text-xs">
                            {interest}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </Card>

                <h2 className="text-2xl font-serif text-primary-900 mb-2">
                  Step 1: Generate AI Clinical Report
                </h2>
                <p className="text-stone-600 mb-6">
                  Review the assessment data below. The AI will generate a comprehensive, neurodiversity-affirming clinical report.
                </p>

                {!generatedReport ? (
                  <>
                    {/* Standardized Test Results */}
                    <Card className="p-4 mb-4 rounded-xl">
                      <Label className="text-sm font-medium mb-3 block">Standardized Assessment Data</Label>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-stone-200">
                              <th className="text-left py-2 px-2 text-stone-500 font-medium">Test</th>
                              <th className="text-left py-2 px-2 text-stone-500 font-medium">Area</th>
                              <th className="text-center py-2 px-2 text-stone-500 font-medium">SS</th>
                              <th className="text-center py-2 px-2 text-stone-500 font-medium">%ile</th>
                              <th className="text-left py-2 px-2 text-stone-500 font-medium">Descriptor</th>
                            </tr>
                          </thead>
                          <tbody>
                            {assessmentData.standardizedTests.map((test, idx) => (
                              <tr key={idx} className="border-b border-stone-100">
                                <td className="py-2 px-2 font-medium text-primary-900">{test.name}</td>
                                <td className="py-2 px-2 text-stone-600">{test.area}</td>
                                <td className="py-2 px-2 text-center">
                                  <Badge variant="outline">{test.standardScore}</Badge>
                                </td>
                                <td className="py-2 px-2 text-center text-stone-600">{test.percentile}%</td>
                                <td className="py-2 px-2">
                                  <Badge className="bg-amber-100 text-amber-700 text-xs">{test.descriptor}</Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Card>
                    
                    {/* Informal Measures */}
                    <Card className="p-4 mb-4 rounded-xl">
                      <Label className="text-sm font-medium mb-3 block">Informal Measures</Label>
                      <div className="space-y-3">
                        {assessmentData.informalMeasures.map((measure, idx) => (
                          <div key={idx} className="bg-stone-50 p-3 rounded-lg">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-primary-900 text-sm">{measure.name}</span>
                              <Badge variant="secondary" className="text-xs">{measure.area}</Badge>
                            </div>
                            <p className="text-sm text-stone-600">{measure.notes}</p>
                          </div>
                        ))}
                      </div>
                    </Card>

                    <Button 
                      onClick={handleGenerateReport} 
                      disabled={loading}
                      className="w-full rounded-full"
                      size="lg"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Generating Report...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Generate Clinical Report
                        </>
                      )}
                    </Button>
                  </>
                ) : (
                  <>
                    <Card className="p-6 rounded-2xl border-green-200 bg-green-50/50 mb-4">
                      <div className="flex items-center gap-2 mb-4">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <h3 className="font-medium text-green-800">Clinical Report Generated!</h3>
                      </div>
                      <div className="bg-white p-4 rounded-xl text-sm text-stone-700 max-h-[400px] overflow-y-auto space-y-4">
                        <div>
                          <h4 className="font-medium text-primary-900 mb-2">Background</h4>
                          <p className="text-stone-600">{generatedReport.sections.background}</p>
                        </div>
                        <div>
                          <h4 className="font-medium text-primary-900 mb-2">Assessment Results</h4>
                          <div className="whitespace-pre-wrap text-stone-600">{generatedReport.sections.assessmentResults}</div>
                        </div>
                        <div>
                          <h4 className="font-medium text-primary-900 mb-2">Clinical Impression</h4>
                          <div className="whitespace-pre-wrap text-stone-600">{generatedReport.sections.clinicalImpression}</div>
                        </div>
                        <div>
                          <h4 className="font-medium text-primary-900 mb-2">Recommendations</h4>
                          <div className="whitespace-pre-wrap text-stone-600">{generatedReport.sections.recommendations}</div>
                        </div>
                      </div>
                      <div className="mt-3 p-3 bg-purple-50 rounded-lg border border-purple-100">
                        <p className="text-xs text-purple-700">
                          <strong>Note:</strong> This report uses neurodiversity-affirming language and recognizes Alex's gestalt language processing as a valid path to language development.
                        </p>
                      </div>
                    </Card>

                    <Button onClick={goToNextStep} className="w-full rounded-full" size="lg">
                      Continue to SOAP Note <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </>
                )}
              </motion.div>
            )}

            {/* Step 2: SOAP Note */}
            {currentStep === 1 && (
              <motion.div
                key="soap"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h2 className="text-2xl font-serif text-primary-900 mb-2">
                  Step 2: Save SOAP Note
                </h2>
                <p className="text-stone-600 mb-6">
                  Your report data has been formatted into a SOAP note. Review and save.
                </p>

                {!soapSaved ? (
                  <>
                    <div className="space-y-4 mb-6">
                      {[
                        { key: 'subjective', label: 'Subjective', icon: '👤' },
                        { key: 'objective', label: 'Objective', icon: '📊' },
                        { key: 'assessment', label: 'Assessment', icon: '📋' },
                        { key: 'plan', label: 'Plan', icon: '🎯' }
                      ].map(field => (
                        <Card key={field.key} className="p-4 rounded-xl">
                          <Label className="text-sm font-medium mb-2 flex items-center gap-2">
                            <span>{field.icon}</span> {field.label}
                          </Label>
                          <Textarea
                            value={soapNote[field.key]}
                            onChange={(e) => setSoapNote({ ...soapNote, [field.key]: e.target.value })}
                            className="rounded-lg min-h-[60px] text-sm"
                          />
                        </Card>
                      ))}
                    </div>

                    {/* Parent Feedback Section */}
                    <Card className="p-4 rounded-xl bg-purple-50/50 border border-purple-200">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <MessageSquare className="w-4 h-4 text-purple-600" />
                          <Label className="text-sm font-medium text-purple-900">Summary for Parent</Label>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleGenerateParentSummary}
                          disabled={generatingSummary}
                          className="rounded-full text-purple-700 border-purple-200 hover:bg-purple-100"
                          data-testid="generate-parent-summary-btn"
                        >
                          {generatingSummary ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Generating...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4 mr-2" />
                              Generate AI Summary
                            </>
                          )}
                        </Button>
                      </div>
                      
                      <Textarea
                        value={parentSummary}
                        onChange={(e) => setParentSummary(e.target.value)}
                        placeholder="Click 'Generate AI Summary' to create a parent-friendly summary of today's session..."
                        className="min-h-24 rounded-xl bg-white text-sm"
                      />
                      
                      {parentSummary && (
                        <div className="flex items-center justify-between p-3 bg-white rounded-xl mt-3 border border-purple-100">
                          <div className="flex items-center gap-3">
                            <Share2 className="w-5 h-5 text-blue-600" />
                            <div>
                              <Label htmlFor="share-toggle" className="text-sm font-medium cursor-pointer">
                                Share with parent
                              </Label>
                              <p className="text-xs text-stone-500">Parent will receive this summary</p>
                            </div>
                          </div>
                          <Switch
                            id="share-toggle"
                            checked={shareWithParent}
                            onCheckedChange={(checked) => {
                              setShareWithParent(checked);
                              if (checked) {
                                toast.success('Summary will be shared with parent!');
                              }
                            }}
                          />
                        </div>
                      )}
                    </Card>

                    <Button 
                      onClick={handleSaveSoap} 
                      disabled={loading}
                      className="w-full rounded-full"
                      size="lg"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <ClipboardCheck className="w-4 h-4 mr-2" />
                          Save SOAP Note
                        </>
                      )}
                    </Button>
                  </>
                ) : (
                  <>
                    <Card className="p-6 rounded-2xl border-green-200 bg-green-50/50 mb-4">
                      <div className="flex items-center gap-2 mb-4">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <h3 className="font-medium text-green-800">SOAP Note Saved!</h3>
                      </div>
                      
                      {/* Daysheet Connection Highlight */}
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                            <Calendar className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-blue-900">Auto-added to Daysheet!</p>
                            <p className="text-sm text-blue-700">
                              {DEMO_CHILD.name} now appears on today's schedule with status "Completed"
                            </p>
                          </div>
                        </div>
                        
                        <div className="mt-3 bg-white rounded-lg p-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className="text-sm text-stone-500">Today</span>
                              <span className="font-medium text-primary-900">{DEMO_CHILD.name}</span>
                            </div>
                            <Badge className="bg-green-100 text-green-700">Completed</Badge>
                          </div>
                        </div>
                      </div>
                    </Card>

                    <Button onClick={goToNextStep} className="w-full rounded-full" size="lg">
                      Continue to Activities <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </>
                )}
              </motion.div>
            )}

            {/* Step 3: Activities */}
            {currentStep === 2 && (
              <motion.div
                key="activities"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h2 className="text-2xl font-serif text-primary-900 mb-2">
                  Step 3: AI Activity Generator
                </h2>
                <p className="text-stone-600 mb-6">
                  Generate personalized activities based on {DEMO_CHILD.name}'s goals and interests.
                </p>

                {generatedActivities.length === 0 ? (
                  <>
                    <Card className="p-4 mb-6 rounded-xl bg-stone-50">
                      <h3 className="font-medium text-primary-900 mb-3">Activity will be based on:</h3>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-xs text-stone-500 mb-1">Goals</p>
                          <ul className="text-sm text-stone-700 space-y-1">
                            {DEMO_CHILD.goals.map((goal, i) => (
                              <li key={i} className="flex items-center gap-1">
                                <Target className="w-3 h-3 text-primary" /> {goal}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <p className="text-xs text-stone-500 mb-1">Interests</p>
                          <div className="flex flex-wrap gap-1">
                            {DEMO_CHILD.interests.map(interest => (
                              <Badge key={interest} variant="secondary" className="text-xs">
                                {interest}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </Card>

                    <Button 
                      onClick={handleGenerateActivities} 
                      disabled={loading}
                      className="w-full rounded-full"
                      size="lg"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Generating Activities...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Generate Activities
                        </>
                      )}
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="space-y-4 mb-6">
                      {generatedActivities.map((activity, idx) => (
                        <Card key={idx} className="p-4 rounded-xl">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary font-medium">
                              {idx + 1}
                            </div>
                            <div className="flex-1">
                              <h3 className="font-medium text-primary-900 mb-1">{activity.title}</h3>
                              <p className="text-sm text-stone-600 mb-3">{activity.description}</p>
                              <div className="flex flex-wrap gap-2">
                                {activity.goals.map((goal, i) => (
                                  <Badge key={i} variant="outline" className="text-xs">
                                    {goal}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>

                    <Button onClick={goToNextStep} className="w-full rounded-full" size="lg">
                      Continue to Scheduling <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </>
                )}
              </motion.div>
            )}

            {/* Step 4: Scheduling with Daysheet View */}
            {currentStep === 3 && (
              <motion.div
                key="scheduling"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h2 className="text-2xl font-serif text-primary-900 mb-2">
                  Step 4: Your Weekly Daysheet
                </h2>
                <p className="text-stone-600 mb-6">
                  Your SOAP note auto-populated the daysheet. Schedule {DEMO_CHILD.name}'s next session by dragging or selecting a time slot.
                </p>

                {!appointmentConfirmed ? (
                  <>
                    {/* Mini Daysheet Week View */}
                    <Card className="p-4 rounded-xl mb-6 overflow-x-auto">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-medium text-primary-900 flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          This Week's Schedule
                        </h3>
                        <Badge variant="secondary" className="text-xs">
                          Demo View
                        </Badge>
                      </div>
                      
                      {/* Week Grid */}
                      <div className="grid grid-cols-5 gap-2 min-w-[600px]">
                        {DAYS_OF_WEEK.map((day, dayIdx) => (
                          <div key={day} className="min-w-[110px]">
                            <div className={`text-center py-2 rounded-t-lg text-sm font-medium ${
                              dayIdx === (new Date().getDay() === 0 ? 4 : new Date().getDay() - 1) 
                                ? 'bg-primary text-white' 
                                : 'bg-stone-100 text-stone-700'
                            }`}>
                              {day}
                            </div>
                            <div className="border border-t-0 border-stone-200 rounded-b-lg min-h-[120px] p-2 bg-white">
                              {/* Show auto-populated entry from SOAP on today */}
                              {daysheetEntries.filter(e => e.dayIndex === dayIdx).map(entry => (
                                <div 
                                  key={entry.id}
                                  className={`p-2 rounded-lg mb-2 text-xs ${
                                    entry.status === 'completed' 
                                      ? 'bg-green-50 border border-green-200' 
                                      : 'bg-blue-50 border border-blue-200'
                                  }`}
                                >
                                  <div className="flex items-center gap-1 mb-1">
                                    <Clock className="w-3 h-3 text-stone-500" />
                                    <span className="text-stone-600">{entry.time}</span>
                                  </div>
                                  <p className="font-medium text-stone-900 truncate">{entry.childName}</p>
                                  <div className="flex items-center gap-1 mt-1">
                                    {entry.fromSoap && (
                                      <Badge className="text-[10px] bg-green-100 text-green-700 px-1">
                                        From SOAP
                                      </Badge>
                                    )}
                                    <Badge className={`text-[10px] px-1 ${
                                      entry.status === 'completed' 
                                        ? 'bg-green-100 text-green-700' 
                                        : 'bg-blue-100 text-blue-700'
                                    }`}>
                                      {entry.status}
                                    </Badge>
                                  </div>
                                </div>
                              ))}
                              
                              {/* Empty slot indicator */}
                              {daysheetEntries.filter(e => e.dayIndex === dayIdx).length === 0 && (
                                <div className="text-center py-4 text-stone-400 text-xs">
                                  No sessions
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Legend */}
                      <div className="flex items-center gap-4 mt-3 text-xs text-stone-500">
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 rounded bg-green-100 border border-green-200"></div>
                          <span>Completed</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 rounded bg-blue-100 border border-blue-200"></div>
                          <span>Scheduled</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <GripVertical className="w-3 h-3" />
                          <span>Drag to reschedule</span>
                        </div>
                      </div>
                    </Card>

                    {/* Schedule New Appointment */}
                    <Card className="p-6 rounded-xl mb-6">
                      <h3 className="font-medium text-primary-900 mb-4 flex items-center gap-2">
                        <Plus className="w-4 h-4" />
                        Schedule Next Session
                      </h3>
                      
                      <div className="flex items-center gap-3 mb-4 p-3 bg-stone-50 rounded-lg">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-primary-900">{DEMO_CHILD.name}</p>
                          <p className="text-sm text-stone-500">Weekly session</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Date</Label>
                          <Input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            className="rounded-lg"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Time</Label>
                          <Select value={selectedTime} onValueChange={setSelectedTime}>
                            <SelectTrigger className="rounded-lg">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {['09:00', '09:30', '10:00', '10:30', '11:00', '11:30', '14:00', '14:30', '15:00', '15:30'].map(time => (
                                <SelectItem key={time} value={time}>{time}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </Card>

                    <Button 
                      onClick={handleConfirmAppointment} 
                      disabled={loading || !selectedDate}
                      className="w-full rounded-full"
                      size="lg"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Adding to Daysheet...
                        </>
                      ) : (
                        <>
                          <Calendar className="w-4 h-4 mr-2" />
                          Add to Daysheet
                        </>
                      )}
                    </Button>
                  </>
                ) : (
                  <>
                    {/* Demo Complete */}
                    <Card className="p-8 rounded-2xl bg-gradient-to-br from-primary/5 to-primary/10 border-0 text-center">
                      <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                        <PartyPopper className="w-8 h-8 text-green-600" />
                      </div>
                      
                      <h3 className="text-2xl font-serif text-primary-900 mb-2">
                        Demo Complete!
                      </h3>
                      
                      <p className="text-stone-600 mb-6">
                        You've experienced the full PlayFirst workflow: Report → SOAP Note → Activities → Scheduling
                      </p>
                      
                      <div className="bg-white rounded-xl p-4 mb-6 text-left">
                        <p className="text-sm font-medium text-primary-900 mb-3">What you accomplished:</p>
                        <ul className="space-y-2">
                          {[
                            'Generated an AI-powered clinical report',
                            'Saved a SOAP note (auto-added to daysheet)',
                            'Created personalized therapy activities',
                            'Scheduled the next appointment'
                          ].map((item, i) => (
                            <li key={i} className="flex items-center gap-2 text-sm text-stone-600">
                              <CheckCircle className="w-4 h-4 text-green-600" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <p className="text-primary-900 font-medium mb-4">
                        Create your free account to start using PlayFirst with your own clients
                      </p>
                      
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Button onClick={onComplete} size="lg" className="rounded-full">
                          Sign Up Free <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                        <Button onClick={onComplete} variant="outline" size="lg" className="rounded-full">
                          Log In
                        </Button>
                      </div>
                    </Card>
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>

      {/* Bottom Navigation */}
      {currentStep > 0 && !(currentStep === 3 && appointmentConfirmed) && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-stone-100 p-4">
          <div className="max-w-4xl mx-auto">
            <Button variant="ghost" onClick={goToPrevStep} className="rounded-full">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          </div>
        </div>
      )}

      {/* Signup Prompt Dialog */}
      <Dialog open={showSignupPrompt} onOpenChange={setShowSignupPrompt}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-center font-serif text-xl flex items-center justify-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              Enjoying PlayFirst?
            </DialogTitle>
          </DialogHeader>
          
          <div className="text-center text-stone-600 py-4">
            {signupPromptMessage}
          </div>
          
          <div className="space-y-3">
            <Button 
              onClick={onComplete} 
              className="w-full rounded-full bg-primary hover:bg-primary/90"
              data-testid="signup-now-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Sign Up Free
            </Button>
            
            <Button 
              variant="ghost" 
              onClick={continueToNextStep} 
              className="w-full rounded-full text-stone-500"
              data-testid="continue-demo-btn"
            >
              Continue Exploring Demo
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          
          <p className="text-xs text-center text-stone-400 mt-2">
            Free plan includes 3 children, 3 AI reports, and all core features
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
